import { jwtDecode } from 'jwt-decode';

const ACCESS_TOKEN_KEY = 'ecole_futee_access_token';
const REFRESH_TOKEN_KEY = 'ecole_futee_refresh_token';
const ORIGINAL_ACCESS_TOKEN_KEY = 'ecole_futee_original_access_token';
const ORIGINAL_REFRESH_TOKEN_KEY = 'ecole_futee_original_refresh_token';

export interface ImpersonatorInfo {
  id: number;
  username: string;
}

export interface JWTPayload {
  exp: number;
  iat: number;
  id: number;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  fullname: string;
  roles: string[];
  /** Present when impersonating another user */
  impersonator?: ImpersonatorInfo;
}

export const tokenStorage = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_ACCESS_TOKEN_KEY);
    localStorage.removeItem(ORIGINAL_REFRESH_TOKEN_KEY);
  },

  isTokenExpired: (token: string): boolean => {
    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime + 30;
    } catch {
      return true;
    }
  },

  getTokenPayload: (token: string): JWTPayload | null => {
    try {
      return jwtDecode<JWTPayload>(token);
    } catch {
      return null;
    }
  },

  getUserFromToken: (): JWTPayload | null => {
    const token = tokenStorage.getAccessToken();
    if (!token) return null;
    return tokenStorage.getTokenPayload(token);
  },

  // Impersonation helpers
  isImpersonating: (): boolean => {
    const token = tokenStorage.getAccessToken();
    if (!token) return false;
    const payload = tokenStorage.getTokenPayload(token);
    return !!payload?.impersonator;
  },

  getImpersonator: (): ImpersonatorInfo | null => {
    const token = tokenStorage.getAccessToken();
    if (!token) return null;
    const payload = tokenStorage.getTokenPayload(token);
    return payload?.impersonator || null;
  },

  /** Store original tokens before impersonation */
  storeOriginalTokens: (): void => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (accessToken) {
      localStorage.setItem(ORIGINAL_ACCESS_TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(ORIGINAL_REFRESH_TOKEN_KEY, refreshToken);
    }
  },

  /** Restore original tokens after exiting impersonation */
  restoreOriginalTokens: (): boolean => {
    const originalAccessToken = localStorage.getItem(ORIGINAL_ACCESS_TOKEN_KEY);
    const originalRefreshToken = localStorage.getItem(ORIGINAL_REFRESH_TOKEN_KEY);

    if (originalAccessToken && originalRefreshToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, originalAccessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, originalRefreshToken);
      localStorage.removeItem(ORIGINAL_ACCESS_TOKEN_KEY);
      localStorage.removeItem(ORIGINAL_REFRESH_TOKEN_KEY);
      return true;
    }
    return false;
  },

  hasOriginalTokens: (): boolean => {
    return !!localStorage.getItem(ORIGINAL_ACCESS_TOKEN_KEY);
  },
};
